import { Link } from 'react-router-dom';
import { useProducts } from '../../../hooks/useProducts';
import { useLang } from '../../../context/LanguageContext';
import { useCart } from '../../../context/CartContext';
import { API_CONFIG } from '../../../services/config';
import type { Product } from '../../../types/product';

const LOGO_PLACEHOLDER = 'https://storage.readdy-site.link/project_files/e0ad0187-0f87-44ac-bc81-3163fcc0baf7/856a6a4e-c760-477d-a837-65755837c797_fb6affb0-c65b-4aac-81c4-bfd5377e0e55.webp?v=328770f95ae230a12f1326c5fd47e1ad';

export default function LatestProducts() {
  const { products, loading } = useProducts();
  const { t } = useLang();
  const { addItem } = useCart();

  // Show first 3 products
  const display = products.slice(0, 3);

  return (
    <section className="w-full bg-white py-16 md:py-20 px-4 sm:px-6" data-product-shop>
      <div className="max-w-screen-xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-10 md:mb-12">
          <span className="text-red-600 text-[10px] font-extrabold uppercase tracking-[0.3em]">
            {t('latest_badge')}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 mt-2">
            {t('latest_title1')} <span className="text-red-600">{t('latest_title2')}</span>
          </h2>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-zinc-100">
                <div className="w-full h-48 bg-zinc-100 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-2.5 bg-zinc-100 animate-pulse rounded-full w-2/3" />
                  <div className="h-2 bg-zinc-100 animate-pulse rounded-full w-1/2" />
                  <div className="h-8 bg-zinc-100 animate-pulse rounded-lg mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {display.map((product) => {
              const imageSrc = product.images?.[0] ?? '';
              const hasImage = imageSrc.trim().length > 0;

              return (
                <div
                  key={product.id}
                  className="group border border-zinc-100 rounded-xl overflow-hidden flex flex-col hover:border-zinc-200 transition-colors duration-300"
                >
                  {/* Image */}
                  <div className="relative w-full h-44 sm:h-52 overflow-hidden bg-white">
                    <Link to={`/products/${API_CONFIG.COMPANY_ID}/${product.id}`} className="block w-full h-full cursor-pointer">
                      {hasImage ? (
                        <img
                          src={imageSrc}
                          alt={product.name}
                          className="w-full h-full object-contain p-3 sm:p-4 group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-8 sm:p-10 bg-zinc-50">
                          <img
                            src={LOGO_PLACEHOLDER}
                            alt={product.name}
                            className="max-w-[50%] max-h-[50%] object-contain opacity-50"
                            loading="lazy"
                            draggable={false}
                          />
                        </div>
                      )}
                    </Link>

                    {/* Price badge */}
                    {product.price && (
                      <div className="absolute top-2 right-2">
                        <span className="bg-red-600 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-md shadow-sm">
                          RD${product.price.toLocaleString('es-DO')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  <div className="p-4 flex flex-col flex-1">
                    <Link
                      to={`/products/${API_CONFIG.COMPANY_ID}/${product.id}`}
                      className="cursor-pointer"
                    >
                      <h3 className="text-zinc-800 font-medium text-sm leading-snug mb-1.5 line-clamp-2 group-hover:text-red-600 transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    {product.brand && (
                      <p className="text-zinc-400 text-[10px] font-medium uppercase tracking-wider mb-2">
                        {product.brand}
                      </p>
                    )}

                    <div className="flex items-center gap-2 mt-auto">
                      <button
                        onClick={() => addItem(product as Product)}
                        className="flex-1 bg-neutral-900 hover:bg-red-600 text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-2 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                      >
                        <i className="ri-shopping-cart-line text-sm" />
                        {t('add_to_cart')}
                      </button>
                      <Link
                        to={`/products/${API_CONFIG.COMPANY_ID}/${product.id}`}
                        className="w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-400 hover:border-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer"
                        title="Ver detalle"
                      >
                        <i className="ri-eye-line text-sm" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-900 text-xs font-medium transition-colors cursor-pointer"
          >
            {t('latest_view_all')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
